import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { AxiosError } from "axios";
import { Paperclip } from "@phosphor-icons/react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Field } from "@libs/zapi-ui/ds/components/field";
import { Label } from "@libs/zapi-ui/ds/components/label";
import { Sheet, SheetContent, SheetTitle } from "@libs/zapi-ui/ds/components/sheet";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { Textarea } from "@libs/zapi-ui/ds/components/textarea";
import { AttachmentItem } from "@/zapi-frontend/shared/ui/chat/AttachmentItem";
import type { TRisk, TRiskFile, TRiskInput } from "@/zapi-frontend/shared/types/risks";
import { getRiskFileUrl, useAddressRisk } from "./useRisks";
import { RiskFields, isRiskInputComplete, toRiskInput } from "./_RiskFields";
import { getMalwareScanMessage } from "@/zapi-frontend/shared/lib/malwareScanError";

type TAddressRiskSheetProps = {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  risk: TRisk | null;
};

export function AddressRiskSheet({ projectId, open, onOpenChange, risk }: TAddressRiskSheetProps) {
  return (
    <Sheet open={open && !!risk} onOpenChange={onOpenChange}>
      {risk && <AddressRiskSheetContent projectId={projectId} open={open} onOpenChange={onOpenChange} risk={risk} />}
    </Sheet>
  );
}

function AddressRiskSheetContent({
  projectId,
  open,
  onOpenChange,
  risk
}: Omit<TAddressRiskSheetProps, "risk"> & { risk: TRisk }) {
  const { t } = useTranslation(["projectHome", "common"]);
  const addressRisk = useAddressRisk(projectId);

  const [plan, setPlan] = useState("");
  const [results, setResults] = useState("");
  const [planFiles, setPlanFiles] = useState<File[]>([]);
  const [resultsFiles, setResultsFiles] = useState<File[]>([]);
  const [removedFileIds, setRemovedFileIds] = useState<number[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [riskInput, setRiskInput] = useState<TRiskInput>(toRiskInput());
  const planInputRef = useRef<HTMLInputElement>(null);
  const resultsInputRef = useRef<HTMLInputElement>(null);

  const isAddressed = risk.status === "addressed";
  const isReadOnly = isAddressed && !isEditing;

  const resetForm = () => {
    setPlan(risk.plan ?? "");
    setResults(risk.results ?? "");
    setPlanFiles([]);
    setResultsFiles([]);
    setRemovedFileIds([]);
    setRiskInput(toRiskInput(risk));
    setIsEditing(false);
  };

  useEffect(() => {
    if (open) resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, risk]);

  const openFile = async (file: TRiskFile) => {
    // Opened synchronously so the popup blocker treats it as part of the click.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    try {
      const url = await getRiskFileUrl(projectId, risk.id, file.id);
      if (tab) tab.location.href = url;
      else window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      tab?.close();
      toast.error(getMalwareScanMessage(error) ?? t("risks.fileOpenFailed"));
    }
  };

  const handleSubmit = async () => {
    try {
      await addressRisk.mutateAsync({
        riskId: risk.id,
        plan,
        results,
        planFiles,
        resultsFiles,
        removeFileIds: removedFileIds,
        riskFields: isEditing ? riskInput : undefined
      });
      toast.success(isAddressed ? t("risks.updated") : t("risks.addressed"));
      onOpenChange(false);
    } catch (err) {
      toast.error((err as AxiosError<{ message?: string }>).response?.data?.message ?? t("risks.saveFailed"));
    }
  };

  const stagedFiles = (files: File[], onRemove: (index: number) => void) =>
    files.map((file, index) => (
      <AttachmentItem key={`${file.name}-${index}`} kind="file" label={file.name} onRemove={() => onRemove(index)} />
    ));

  const keptFiles = (section: "plan" | "results") =>
    risk.files.filter((file) => file.section === section && !removedFileIds.includes(file.id));

  const removableSavedFiles = (section: "plan" | "results") =>
    keptFiles(section).map((file) => (
      <AttachmentItem
        key={file.id}
        kind="file"
        label={file.fileName}
        onRemove={() => setRemovedFileIds([...removedFileIds, file.id])}
      />
    ));

  const savedFiles = (section: "plan" | "results") =>
    keptFiles(section).map((file) => (
      <button
        key={file.id}
        type="button"
        className="w-full cursor-pointer rounded-md text-left transition-colors hover:bg-innoveraZinc-50 hover:underline"
        onClick={() => openFile(file)}
      >
        <AttachmentItem kind="file" label={file.fileName} />
      </button>
    ));

  const section = (
    label: string,
    placeholder: string,
    value: string,
    setValue: (value: string) => void,
    files: File[],
    setFiles: (files: File[]) => void,
    inputRef: React.RefObject<HTMLInputElement>,
    sectionKey: "plan" | "results"
  ) => (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        {!isReadOnly && (
          <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
            <Paperclip className="size-3" />
            {t("risks.attachFile")}
          </Button>
        )}
      </div>
      {isReadOnly ? (
        <FigmaTypography variant="body-2" color="zinc900" className="whitespace-pre-wrap">
          {value || "—"}
        </FigmaTypography>
      ) : (
        <>
          <Field className="w-full gap-2">
            <Textarea
              className="h-[120px] resize-none"
              placeholder={placeholder}
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
          </Field>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(event) => {
              setFiles([...files, ...Array.from(event.target.files ?? [])]);
              event.target.value = "";
            }}
          />
        </>
      )}
      <div className="flex flex-col gap-2">
        {isReadOnly ? (
          savedFiles(sectionKey)
        ) : (
          <>
            {removableSavedFiles(sectionKey)}
            {stagedFiles(files, (index) => setFiles(files.filter((_, i) => i !== index)))}
          </>
        )}
      </div>
    </div>
  );

  return (
    <SheetContent
      closeLabel={t("common:close")}
      className="flex w-[440px] max-w-full flex-col gap-0 p-0 sm:max-w-[440px]"
    >
      <div className="flex h-full flex-col gap-5 overflow-y-auto p-5 custom-thin-scrollbar">
        <SheetTitle className="font-medium tracking-[-0.2px]">
          {isEditing ? t("risks.editTitle") : t("risks.addressTitle")}
        </SheetTitle>

        {isEditing ? (
          <RiskFields value={riskInput} onChange={setRiskInput} />
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <Label>{t("risks.fieldWhatMustHoldTrue")}</Label>
              <FigmaTypography variant="body-2" color="zinc900">
                {risk.whatMustHoldTrue}
              </FigmaTypography>
            </div>

            <div className="flex flex-col gap-1">
              <Label>{t("risks.fieldWhyItMatters")}</Label>
              <FigmaTypography variant="body-2" color="zinc900">
                {risk.whyItMatters}
              </FigmaTypography>
            </div>
          </>
        )}

        <div className="h-px w-full bg-innoveraZinc-100" />

        {section(
          t("risks.plan"),
          t("risks.planPlaceholder"),
          plan,
          setPlan,
          planFiles,
          setPlanFiles,
          planInputRef,
          "plan"
        )}
        {section(
          t("risks.results"),
          t("risks.resultsPlaceholder"),
          results,
          setResults,
          resultsFiles,
          setResultsFiles,
          resultsInputRef,
          "results"
        )}

        {isReadOnly ? (
          <div>
            <Button type="button" variant="outline" onClick={() => setIsEditing(true)}>
              {t("risks.edit")}
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={
                addressRisk.isPending ||
                !plan.trim() ||
                !results.trim() ||
                (isEditing && !isRiskInputComplete(riskInput))
              }
            >
              {addressRisk.isPending ? (
                <>
                  <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                  {t("common:loading")}
                </>
              ) : isAddressed ? (
                t("risks.save")
              ) : (
                t("risks.submit")
              )}
            </Button>
            {isEditing && (
              <Button type="button" variant="outline" onClick={resetForm}>
                {t("risks.cancel")}
              </Button>
            )}
          </div>
        )}
      </div>
    </SheetContent>
  );
}
